import { useEffect, useState } from "react";

// Hooks & Helpers
import { useToast } from "sj-ui-toolkit/src/hooks";
import {
  fetchMentorGuides,
  saveMentorGuides,
  restoreMentorGuides,
  type MentorGuide,
} from "@/api/activities";

// Local Components
import TabSkeleton from "./TabSkeleton";

// 3rd Party Imports
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Card } from "primereact/card";
import { OrderList } from "primereact/orderlist";
import { confirmDialog } from "primereact/confirmdialog";

interface MentorGuidesTabProps {
  onUnsavedChange?: (hasChanges: boolean) => void;
}

export default function MentorGuidesTab({ onUnsavedChange }: MentorGuidesTabProps) {
  const [mentorGuides, setMentorGuides] = useState<MentorGuide[]>([]);
  const [maxMentorGuideId, setMaxMentorGuideId] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { showToast } = useToast();

  const markChanged = () => onUnsavedChange?.(true);
  const markSaved = () => onUnsavedChange?.(false);

  useEffect(() => {
    const loadMentorGuides = async () => {
      try {
        setIsLoading(true);
        const data = await fetchMentorGuides();
        setMentorGuides(data.mentorGuides || []);
        setMaxMentorGuideId(data.maxMentorGuideId || 0);
      } catch (error) {
        console.error("Failed to load mentor guides:", error);
        showToast({ severity: "error", detail: "Failed to load mentor guides" });
      } finally {
        setIsLoading(false);
      }
    };

    loadMentorGuides();
  }, []);

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      const data = await saveMentorGuides(mentorGuides);
      setMentorGuides(data.mentorGuides || []);
      setMaxMentorGuideId(data.maxMentorGuideId || 0);
      markSaved();
      showToast({ severity: "success", detail: "Mentor guides saved successfully" });
    } catch (error) {
      console.error("Failed to save mentor guides:", error);
      showToast({ severity: "error", detail: "Failed to save mentor guides" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleRestore = () => {
    confirmDialog({
      message:
        "If you proceed, you will lose saved changes and have Security Journey default configurations restored.",
      header: "Are you sure?",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          const data = await restoreMentorGuides();
          setMentorGuides(data.mentorGuides || []);
          setMaxMentorGuideId(data.maxMentorGuideId || 0);
          markSaved();
          showToast({ severity: "success", detail: "Mentor guides restored to defaults" });
        } catch (error) {
          console.error("Failed to restore mentor guides:", error);
          showToast({ severity: "error", detail: "Failed to restore mentor guides" });
        }
      },
    });
  };

  const addMentorGuide = () => {
    const newId = maxMentorGuideId + 1;
    setMentorGuides([...mentorGuides, { id: newId, name: "" }]);
    setMaxMentorGuideId(newId);
    markChanged();
  };

  const updateMentorGuide = (id: number, name: string) => {
    setMentorGuides(mentorGuides.map(guide => (guide.id === id ? { ...guide, name } : guide)));
    markChanged();
  };

  const deleteMentorGuide = (id: number) => {
    setMentorGuides(mentorGuides.filter(guide => guide.id !== id));
    markChanged();
  };

  const mentorGuideTemplate = (guide: MentorGuide) => {
    return (
      <div className="flex align-items-center gap-3 w-full">
        <InputText
          value={guide.name}
          onChange={e => updateMentorGuide(guide.id, e.target.value)}
          placeholder="Mentor guide name"
          className="flex-1"
          maxLength={45}
          autoFocus={guide.name === ""}
        />
        <Button
          icon="pi pi-trash"
          onClick={() => deleteMentorGuide(guide.id)}
          severity="danger"
          text
          rounded
        />
      </div>
    );
  };

  if (isLoading) {
    return <TabSkeleton rows={5} />;
  }

  return (
    <div className="p-4">
      <Card>
        <div className="mb-4">
          <p className="text-secondary mb-4">Configure mentor guides for activities.</p>

          <OrderList
            dataKey="id"
            value={mentorGuides}
            onChange={e => {
              setMentorGuides(e.value);
              markChanged();
            }}
            itemTemplate={mentorGuideTemplate}
            dragdrop
            pt={{
              controls: { style: { justifyContent: "flex-start" } },
              list: { style: { maxHeight: "none", overflow: "visible" } },
            }}
          />

          <Button
            label="Add Mentor Guide"
            icon="pi pi-plus"
            onClick={addMentorGuide}
            severity="secondary"
            className="w-fit mt-3"
          />
        </div>

        <div className="flex gap-2 mt-4 justify-end">
          <Button label="Restore Defaults" onClick={handleRestore} severity="secondary" />
          <Button
            label={isSaving ? "Saving..." : "Save"}
            onClick={() => void handleSave()}
            disabled={isSaving}
          />
        </div>
      </Card>
    </div>
  );
}
